import type { BaseFilter, AccountFilter, AccountDropdownFilter } from "./ReportingFilter";

export const dateFilterMissingValues = (selectedData: BaseFilter) =>
  selectedData.dateFrom === "" || selectedData.dateTo === "";

type PropertyMapper = {
  source: string;
  target: string;
  transform?: (value: any, currentFilter: any) => any;
};

const transformDistinct = (value: boolean | undefined) => {
  if (value === true) return "Distinct";
  if (value === false) return "Total";
  return undefined;
};

const transformIsActive = (value: boolean | null | undefined) => {
  if (value === null) return 0;
  if (value === true) return 1;
  if (value === false) return 2;
  return undefined;
};

const FILTER_PROPERTY_MAPPINGS: PropertyMapper[] = [
  {
    source: "showCustomers",
    target: "showCustomers",
  },
  {
    source: "accounts",
    target: "accounts",
  },
  {
    source: "packId",
    target: "packId",
  },
  {
    source: "includeMyData",
    target: "includeMyData",
  },
  {
    source: "isDistinct",
    target: "distinct",
    transform: transformDistinct,
  },
  {
    source: "videoType",
    target: "videoType",
  },
  {
    source: "type",
    target: "type",
  },
  {
    source: "priority",
    target: "priority",
  },
  {
    source: "status",
    target: "status",
  },
  {
    source: "packStatusId",
    target: "packStatusId",
  },
  {
    source: "packTypeId",
    target: "packTypeId",
  },
  {
    source: "addOnId",
    target: "addOnId",
  },
  {
    source: "isActive",
    target: "isActive",
    transform: transformIsActive,
  },
  {
    source: "flowId",
    target: "flowId",
  },
];

export function formatActiveFilter(currentFilter: BaseFilter | AccountFilter | AccountDropdownFilter): any {
  const filter: any = {};

  if (!dateFilterMissingValues(currentFilter)) {
    filter.dates = {
      from: currentFilter.dateFrom,
      to: currentFilter.dateTo,
    };
  }

  for (const mapping of FILTER_PROPERTY_MAPPINGS) {
    const value = (currentFilter as any)[mapping.source];

    if (value === undefined) {
      continue;
    }

    const transformedValue = mapping.transform ? mapping.transform(value, currentFilter) : value;

    if (transformedValue === undefined) {
      continue;
    }

    filter[mapping.target] = transformedValue;
  }

  return filter;
}

export const extractAndValidateDates = (
  filter: any,
  parsedIgnoreInCountAndHide: string[] | undefined,
  initialFilter: { dates: { from: string; to: string } },
): { from: string; to: string } | null => {
  let from = "",
    to = "";

  if (filter.dates) {
    from = filter.dates.from;
    to = filter.dates.to;
    if (dateFilterMissingValues({ dateFrom: from, dateTo: to })) {
      return null;
    }
  }

  if (parsedIgnoreInCountAndHide?.includes("dates")) {
    from = initialFilter.dates.from;
    to = initialFilter.dates.to;
  }

  return { from, to };
};

export const buildFilterCallback = (
  newStateData: any,
  filter: any,
  longFormDates: { dateFrom: string; dateTo: string },
  options: {
    includeAccounts?: boolean;
    includeAccountsDropdown?: boolean;
    includePacks?: boolean;
    includeDistinct?: boolean;
    includeVideoType?: boolean;
    includePriority?: boolean;
    includeStatus?: boolean;
    includeAddons?: boolean;
    includeFlows?: boolean;
  },
) => {
  return {
    dateFrom: longFormDates.dateFrom,
    dateTo: longFormDates.dateTo,
    type: newStateData.type,
    ...(options.includeAccounts && { showCustomers: !!filter.showCustomers }),
    ...(options.includeAccountsDropdown && { includeMyData: newStateData.includeMyData }),
    ...(options.includeAccountsDropdown && { accounts: newStateData.accounts }),
    ...(options.includePacks && { packId: newStateData.packId }),
    ...(options.includePacks && { packStatusId: newStateData.packStatusId }),
    ...(options.includePacks && { packTypeId: newStateData.packTypeId }),
    ...(options.includeDistinct && { isDistinct: filter.distinct === "Distinct" }),
    ...(options.includeVideoType && { videoType: newStateData.videoType }),
    ...(options.includePriority && { priority: newStateData.priority }),
    ...(options.includeStatus && { status: newStateData.status }),
    ...(options.includeAddons && { addOnId: newStateData.addOnId, isActive: newStateData.isActive }),
    ...(options.includeFlows && { flowId: newStateData.flowId }),
  };
};
